import React from 'react';
import { Mail, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import LandingLayout, { useLandingMeta } from './LandingLayout';
import { GlowButton, GlassCard, SectionShell } from './LandingPrimitives';
import { trackLandingEvent } from './analytics';

const SUPPORT_EMAIL = 'customersupport@groundaale.com';

const ContactPage = () => {
  useLandingMeta({
    title: 'Contact Us | Ground Aale',
    description: 'Reach the Ground Aale team for product questions, onboarding help, or partnership enquiries.',
    ogDescription: 'Contact Ground Aale support at customersupport@groundaale.com.',
  });

  return (
    <LandingLayout>
      <SectionShell id="top" className="pt-24 md:pt-28">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-ga-primary">Contact us</p>
        <h1 className="text-3xl font-bold leading-tight text-ga-text-primary md:text-4xl">Talk to the Ground Aale team</h1>
        <p className="mt-4 max-w-3xl text-sm text-ga-text-muted md:text-base">
          Whether you are evaluating Ground Aale for your facility or need help with an existing account, our support
          team is ready to help.
        </p>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <GlassCard>
            <div className="flex items-start gap-3">
              <div className="shrink-0 rounded-lg bg-ga-green/20 p-2">
                <Mail className="h-5 w-5 text-ga-primary" />
              </div>
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ga-text-primary">Email support</h2>
                <p className="mt-2 text-sm text-ga-text-muted">
                  For product questions, onboarding, or account help:
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="mt-3 inline-block text-base font-semibold text-ga-primary hover:text-ga-text-primary"
                  onClick={() => trackLandingEvent('contact_email_click')}
                >
                  {SUPPORT_EMAIL}
                </a>
              </div>
            </div>
          </GlassCard>

          <GlassCard>
            <div className="flex items-start gap-3">
              <div className="shrink-0 rounded-lg bg-ga-green/20 p-2">
                <MessageCircle className="h-5 w-5 text-ga-primary" />
              </div>
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-ga-text-primary">Ready to start?</h2>
                <p className="mt-2 text-sm leading-relaxed text-ga-text-muted">
                  If you already know Ground Aale is right for your ground, you can create an account and begin setup
                  right away.
                </p>
                <GlowButton
                  as={Link}
                  to="/login"
                  className="mt-4"
                  onClick={() => trackLandingEvent('contact_get_started_click')}
                >
                  Get started
                </GlowButton>
              </div>
            </div>
          </GlassCard>
        </div>
      </SectionShell>
    </LandingLayout>
  );
};

export default ContactPage;
